import React, { useMemo, useState } from "react";

import { LoadingIcon, MoreIcon } from "@packages/ds";
import momentjs from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import {
  ACTIVITY_TYPES,
  activityTypeBuilds,
  activityTypeRun,
  MOMENTJS_OPTIONS,
  ACTIVITY_CONTEXT,
  MOMENTJS_TIME_FORMAT
} from "Constants/constants";
import * as Dropdown from "ds/Dropdown";
import { Tooltip } from "ds/Tooltip";
import { useGetUserAvatar } from "Hooks/useGetAvatar";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import logger from "Libs/logger";
import { request } from "Libs/platform";
import { getSelectors } from "Reducers/activity";
import { environmentsAsArraySelector } from "Reducers/environment";
import { organizationByDescriptionIdSelector } from "Reducers/organization/organization";
import { filterRecentActivities } from "src/organization/common/containers/ActivitySection/ActivitySection";
import { useAppSelector } from "Store/hooks";

import * as S from "./Activity.styles";
import ActivityDate from "./ActivityDate";
import CommitList from "./CommitList";
import BackupRestoreModal from "./Modals/BackupRestoreModal";
import CancelActivityModal from "./Modals/CancelActivityModal";
import PopupLog from "./PopupLog/PopupLog";
import StatusIcon from "./StatusIcon/StatusIcon";

import type { Activity as TActivity } from "@packages/client";

type ActivityProps = {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  cancelTitle?: React.ReactNode;
  id?: string;
  link?: React.ReactNode;
  openModal: () => void;
  activity: TActivity;
  canEditProject?: boolean;
  openedCancelModal?: string;
  setOpenedCancelModal?: React.Dispatch<React.SetStateAction<string>>;
  activityDetails?: { activityTitle: string; activityId: string };
  shouldShowLog?: boolean;
  setSelectedFailedActivityLog?: React.Dispatch<React.SetStateAction<string>>;
  selectedFailedActivityLog?: string;
  shouldForceLoader?: boolean;
  notification?: ({
    activity,
    projectId,
    organizationId
  }: {
    activity: TActivity;
    projectId: string;
    organizationId: string;
  }) => void;
};

const cancellableActivityTypes = [
  ACTIVITY_TYPES.ENVIRONMENT.CRON,
  ACTIVITY_TYPES.ENVIRONMENT.SOURCE_OPERATION,
  ACTIVITY_TYPES.ENVIRONMENT.PUSH
];

const Activity = ({
  icon,
  title,
  link,
  openModal,
  activity,
  openedCancelModal,
  setOpenedCancelModal,
  cancelTitle,
  id,
  activityDetails,
  shouldShowLog = false,
  setSelectedFailedActivityLog,
  selectedFailedActivityLog,
  shouldForceLoader,
  notification
}: ActivityProps) => {
  const intl = useIntl();
  momentjs.updateLocale("en", MOMENTJS_OPTIONS);
  const [showCommits, setShowCommits] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const { organizationId, projectId } = useParams<{
    organizationId: string;
    projectId: string;
  }>();

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );

  const { inProgress, completed } = getSelectors(ACTIVITY_CONTEXT.Project, {
    projectId,
    organizationId
  });

  const avatarPicture = useGetUserAvatar({
    user: activity?.payload?.user,
    organization
  });

  const inProgressActivity = useAppSelector(inProgress);

  const inCompletedActivity = useAppSelector(completed);

  const recentInprogressActivities = useMemo(
    () => filterRecentActivities(inProgressActivity),
    [inProgressActivity]
  );

  const recentCompletedActivities = useMemo(
    () => filterRecentActivities(inCompletedActivity),
    [inCompletedActivity]
  );
  const activityId = (activity.id || id) ?? "";
  const isCancelModalOpen = activityId === openedCancelModal;

  const isCron = activity.type === ACTIVITY_TYPES.ENVIRONMENT.CRON;
  const isBackup = activity.type === ACTIVITY_TYPES.ENVIRONMENT.BACKUP;
  const isSourceOperation =
    activity.type === ACTIVITY_TYPES.ENVIRONMENT.SOURCE_OPERATION;
  const isBuildingHook = !activity.timings?.build;

  const isCancellable =
    (activity as any)?._links?.["#cancel"] &&
    activity.state !== "complete" &&
    (activity.state === "pending" ||
      isBuildingHook ||
      isCron ||
      isSourceOperation);

  const shouldHideCancel =
    activity.state === "cancelled" ||
    activity.state === "complete" ||
    (!(activity as any)?._links?.["#cancel"] &&
      activity.state !== "in_progress");
  const shouldRenderCommitsCount = activity.payload?.commits && !showCommits;
  const shouldRenderRestore = isBackup && activity.hasLink;

  notification?.({
    activity,
    projectId: projectId as string,
    organizationId: organizationId as string
  });

  const commits = activity.payload?.commits?.sort(
    (a, b) => b.author.date - a.author.date
  );

  const recentCommit = commits?.[0];

  const duration = useMemo(() => {
    const endTime = activity.completed_at ?? activity.cancelled_at;
    const startTime = activity.started_at ?? activity.created_at;

    const isEndTimeValid = momentjs(endTime).isValid();
    const isStartTimeValid = momentjs(startTime).isValid();

    // In the occasion that the "duration" returns "Invalid date", show nothing
    if (!isEndTimeValid || !isStartTimeValid) return "";

    return momentjs
      .duration(momentjs(endTime).diff(momentjs(startTime)))
      .humanize({ s: 60, m: 60, h: 24, d: 28, M: 11 });
  }, [
    activity.completed_at,
    activity.cancelled_at,
    activity.started_at,
    activity.created_at
  ]);

  const handleToggleCommit = () => {
    setShowCommits(!showCommits);
    (document.activeElement as any)?.blur();
  };

  const handleStopRun = () => {
    request(activity.getLink("#cancel"), "POST").catch(logger);
  };

  const environments = useSelectorWithUrlParams(environmentsAsArraySelector);

  if (!activity) return null;

  const environmentExists = environments.find(env => {
    return env.id === activityDetails?.activityId;
  });

  return (
    <S.ActivityListItemLayout withShadow={showCommits}>
      <S.Wrapper
        onClick={() => setSelectedFailedActivityLog?.(activity.id)}
        $shouldShowLog={shouldShowLog}
      >
        <S.Content>
          <S.Icon data-testid="icon-wrapper">{icon}</S.Icon>
          <S.Avatar
            userName={activity.payload?.user?.display_name}
            userImage={avatarPicture}
            size="small"
            withToolTip
            alt="avatar"
          />
          <S.Event>
            <S.Content>
              {title}
              {shouldRenderCommitsCount && (
                <S.Link tabIndex={0} to="#" onClick={handleToggleCommit}>
                  + {commits ? commits.length : ""}
                </S.Link>
              )}
            </S.Content>
            <S.DetailsWrapper>
              <S.TextWrapper>
                {activityDetails &&
                  (!environmentExists ? (
                    <Tooltip
                      tooltip={intl.formatMessage({
                        id: "environemnt.activity.deleted.tooltip"
                      })}
                      analyticId="environemnt.activity.deleted.tooltip"
                    >
                      {" "}
                      <S.Environment
                        disabled={true}
                        data-testid="environment"
                        to={`/${organizationId}/${projectId}/${encodeURIComponent(
                          activityDetails.activityId
                        )}`}
                      >
                        {activityDetails.activityTitle}
                      </S.Environment>{" "}
                    </Tooltip>
                  ) : (
                    <S.Environment
                      data-testid="environment"
                      to={`/${organizationId}/${projectId}/${encodeURIComponent(
                        activityDetails.activityId
                      )}`}
                    >
                      {activityDetails.activityTitle}
                    </S.Environment>
                  ))}
                {recentCommit && (
                  <>
                    <S.Sha>{recentCommit.sha?.substring(0, 7)}</S.Sha>
                    <S.Message>{recentCommit?.message}</S.Message>
                  </>
                )}
              </S.TextWrapper>
            </S.DetailsWrapper>
          </S.Event>
          <S.Duration>{duration}</S.Duration>
          {activity.state !== "scheduled" && (
            <S.ActivityDate>
              <ActivityDate date={activity.created_at} />
            </S.ActivityDate>
          )}
          <S.AcitivityStatusWrapper>
            <S.ActivityStatus>
              {/* not sure what this link part is doing */}
              {link && activity.result === "success" ? <span>{link}</span> : ""}
              {isBuildingHook &&
                (activityTypeRun as string[]).includes(activity.type) &&
                activity.state === "in_progress" &&
                "Running..."}
              {isBuildingHook &&
                (activityTypeBuilds as string[]).includes(activity.type) &&
                activity.state === "in_progress" &&
                "Building..."}
              {!isBuildingHook &&
                (activityTypeBuilds as string[]).includes(activity.type) &&
                activity.state === "in_progress" &&
                "Deploying..."}
              {activity.state === "scheduled" &&
                activity.type === ACTIVITY_TYPES.MAINTENANCE.UPGRADE && (
                  <S.Note>
                    <FormattedMessage id="activity.scheduled_for" />{" "}
                    {momentjs(
                      (
                        activity.parameters as unknown as {
                          scheduled_date: string;
                        }
                      )?.scheduled_date
                    ).format(MOMENTJS_TIME_FORMAT)}
                  </S.Note>
                )}
            </S.ActivityStatus>
            {shouldForceLoader ? (
              <LoadingIcon data-testid="loading_activity" />
            ) : (
              <StatusIcon
                viewLogs={openModal}
                state={activity.state}
                result={activity.result}
                type={activity.type}
                activity={activity}
              />
            )}
          </S.AcitivityStatusWrapper>
          <Dropdown.Root id="activity">
            <S.DropdownTrigger aria-label="dropdown-trigger">
              <MoreIcon />
            </S.DropdownTrigger>
            <Dropdown.Portal>
              <S.DropdownContent align="end">
                <Dropdown.Item onClick={openModal}>
                  <FormattedMessage
                    id="activity.log_button"
                    defaultMessage="View log"
                  />
                </Dropdown.Item>
                {shouldRenderRestore && (
                  <Dropdown.Item
                    onClick={() => {
                      setShowConfirmModal(true);
                    }}
                  >
                    <FormattedMessage
                      id="activity.restore_button"
                      defaultMessage="Restore"
                    />
                  </Dropdown.Item>
                )}
                {!shouldHideCancel &&
                  (isCancellable ? (
                    <Dropdown.Item
                      onClick={() => {
                        if (isCancellable) {
                          setOpenedCancelModal?.(activityId);
                        }
                      }}
                      disabled={!isCancellable}
                    >
                      <FormattedMessage
                        id="activity.cancel_button"
                        defaultMessage="Cancel"
                      />
                    </Dropdown.Item>
                  ) : (
                    <S.CancelItem
                      onClick={() => {
                        if (isCancellable) {
                          setOpenedCancelModal?.(activityId);
                        }
                      }}
                      disabled={!isCancellable}
                    >
                      <Tooltip
                        align="end"
                        analyticId={
                          (cancellableActivityTypes as string[]).includes(
                            activity.type
                          ) || activity?._links?.["#cancel"]
                            ? "activity.cancellable_tooltip"
                            : "activity.not_cancellable_tooltip"
                        }
                        tooltip={
                          <FormattedMessage
                            id={
                              (cancellableActivityTypes as string[]).includes(
                                activity.type
                              ) || activity?._links?.["#cancel"]
                                ? "activity.cancellable_tooltip"
                                : "activity.not_cancellable_tooltip"
                            }
                          />
                        }
                      >
                        <FormattedMessage
                          id="activity.cancel_button"
                          defaultMessage="Cancel"
                        />
                      </Tooltip>
                    </S.CancelItem>
                  ))}
              </S.DropdownContent>
            </Dropdown.Portal>
          </Dropdown.Root>
        </S.Content>
        {showCommits && (
          <CommitList
            commits={commits}
            isOpen={showCommits}
            close={() => {
              setShowCommits(false);
            }}
          />
        )}
        {shouldRenderRestore && (
          <BackupRestoreModal
            show={showConfirmModal}
            activity={activity}
            setShow={setShowConfirmModal}
            handleRestore={() => activity.restore()}
            shouldRedirect={false}
            backup={undefined}
          />
        )}
      </S.Wrapper>
      {shouldShowLog && selectedFailedActivityLog === activity.id && (
        <PopupLog activity={activity} openModal={openModal} />
      )}
      <CancelActivityModal
        recentInprogressActivities={recentInprogressActivities}
        recentCompletedActivities={recentCompletedActivities}
        isOpen={isCancelModalOpen}
        onOpenChange={setOpenedCancelModal!}
        isCancellable={isCancellable}
        onCancelActivity={handleStopRun}
        id={activityId}
        title={cancelTitle}
        isPending={activity.state === "pending"}
      />
    </S.ActivityListItemLayout>
  );
};

export default Activity;
